/**
 * NavigationEditorPage — admin-only `/admin/navigation` ("Seitenzuordnung").
 *
 * Lets an admin maintain, at runtime, the groups + page assignment per
 * navigation perspective (Tutor/Admin/Betrieb) via drag & drop, with a live
 * preview. All edits go through the pure `navMutations` and the shared
 * `NavConfigContext`, so editing the currently-shown perspective updates the
 * real sidebar immediately.
 *
 * RBAC: gated by `hasRole('admin')`. Native HTML5 drag&drop (no extra lib) —
 * the drag payload is held in a ref so the drop handlers are testable without a
 * real DataTransfer. Keyboard-DnD is a tracked follow-up (see #835).
 */
import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavConfig } from '../contexts/NavConfigContext';
import { Perspective, PERSPECTIVES, PERSPECTIVE_LABELS, TRAY_ID } from '../nav/navTypes';
import {
  DragPayload,
  movePage,
  renameGroup as applyRename,
  deleteGroup as applyDelete,
  togglePageVisibility as applyToggle,
  addGroup as applyAddGroup,
} from '../nav/navMutations';
import NavGroupCard from './navigation-editor/NavGroupCard';
import NavTrayZone from './navigation-editor/NavTrayZone';
import NavLivePreview from './navigation-editor/NavLivePreview';
import { EditorHandlers } from './navigation-editor/editorTypes';

export default function NavigationEditorPage() {
  const { hasRole } = useAuth();
  const { config, setConfig, perspective } = useNavConfig();
  const [editRole, setEditRole] = useState<Perspective>(perspective);
  const dragRef = useRef<DragPayload | null>(null);

  const handlers: EditorHandlers = useMemo(
    () => ({
      beginDrag: (pageId, fromId) => {
        dragRef.current = { pageId, fromId };
      },
      allowDrop: (e) => e.preventDefault(),
      dropOnGroup: (groupId) => {
        const d = dragRef.current;
        if (!d) return;
        setConfig(movePage(config, editRole, d, groupId, null));
        dragRef.current = null;
      },
      dropBeforePage: (groupId, beforePageId) => {
        const d = dragRef.current;
        if (!d) return;
        setConfig(movePage(config, editRole, d, groupId, beforePageId));
        dragRef.current = null;
      },
      dropOnTray: () => {
        const d = dragRef.current;
        if (!d) return;
        setConfig(movePage(config, editRole, d, TRAY_ID, null));
        dragRef.current = null;
      },
      renameGroup: (groupId, name) => setConfig(applyRename(config, editRole, groupId, name)),
      deleteGroup: (groupId) => setConfig(applyDelete(config, editRole, groupId)),
      toggleVisibility: (groupId, pageId) => setConfig(applyToggle(config, editRole, groupId, pageId)),
    }),
    [config, editRole, setConfig]
  );

  if (!hasRole('admin')) {
    return (
      <Box>
        <Alert severity="error">
          Zugriff verweigert. Die Seitenzuordnung ist nur für Administratoren verfügbar.
        </Alert>
      </Box>
    );
  }

  const data = config[editRole];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Seitenzuordnung
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pflege die Navigationsgruppen und die Zuordnung der Seiten pro Rolle. Ziehe Seiten zwischen
        Gruppen, blende sie ein/aus oder verschiebe sie in „Nicht zugeordnet".
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2">Navigation der Rolle:</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={editRole}
          onChange={(_e, value) => value && setEditRole(value as Perspective)}
          aria-label="Bearbeitete Rolle"
        >
          {PERSPECTIVES.map((p) => (
            <ToggleButton key={p} value={p}>
              {PERSPECTIVE_LABELS[p]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setConfig(applyAddGroup(config, editRole))}
        >
          Gruppe hinzufügen
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 280 }}>
          <Box
            data-testid="nav-editor-groups"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))',
              gap: 2,
            }}
          >
            {data.groups.map((g) => (
              <NavGroupCard key={g.id} group={g} handlers={handlers} />
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <NavTrayZone tray={data.tray} handlers={handlers} />
          </Box>
        </Box>
        <Box sx={{ width: 248 }}>
          <NavLivePreview config={config} editRole={editRole} />
        </Box>
      </Box>
    </Box>
  );
}
